import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'next-i18next';
import '../constants/events.quiz-classroom-create-content';
import { trackEvent } from '@zep/logger/tracking';
import { ZepQuizApiError } from '@/api';
import { useLinkContent } from '@/api/gen/classroom/classroom.gen';
import { useJoinWithLocale } from '@/hooks';
import { ClassroomContentLinkedToast } from '../components/ClassroomContentLinkedToast';
import { type ContentShareType } from '../types';
import { handleClassroomLinkError, invalidateClassroomMutationQueries } from '../utils';

/**
 * 연결 실패 사유를 OTel-safe 문자열로 요약한다. PII 없이 http status 만 남긴다.
 * (docs/event-tracking/guide.md §2.3)
 */
const buildFailReason = (error: unknown): string => {
  if (error instanceof ZepQuizApiError) {
    return typeof error.httpStatus === 'number' ? `http_${error.httpStatus}` : 'unknown';
  }
  if (isAxiosError(error)) {
    return typeof error.response?.status === 'number' ? `http_${error.response.status}` : 'network';
  }
  if (error && typeof error === 'object' && 'status' in error) {
    return `http_${String(error.status)}`;
  }
  return 'unknown';
};

/**
 * 학급 맥락에서 생성된 콘텐츠를 그 학급에 자동 연결한다.
 *
 * 학급 화면의 "새로 만들기"로 진입해 만든 경우에만 호출된다. 생성 화면에
 * 직접 들어와 만든 콘텐츠는 호출부에서 classroomId 가 없어 연결되지 않는다.
 *
 * ⚠️ 실패해도 throw 하지 않는다. 콘텐츠는 이미 만들어진 상태라, 연결 실패로
 * 이동까지 막으면 유저가 방금 만든 결과물을 잃은 것처럼 보인다. 대신 실패를
 * 토스트로 드러내 수동 연결을 유도한다(조용한 실패 금지).
 *
 * page-first: 학급 mutation wrapper 는 `pages/classrooms/**` 에 있어 다른 page
 * 에서 import 할 수 없다. 그래서 raw mutation + 공용 invalidate 유틸을 쓴다
 * (useClassroomLinking 과 동일한 방식).
 */
export function useAutoLinkToClassroom() {
  const queryClient = useQueryClient();
  const {
    t
  } = useTranslation(['common']);
  const classroomPath = useJoinWithLocale();
  const linkMutation = useLinkContent();
  const autoLink = async ({
    classroomId,
    contentId,
    contentType
  }: {
    classroomId: string;
    contentId: string;
    contentType: ContentShareType;
  }) => {
    const trackBase = {
      content_type: (contentType === 'QUIZSET' ? 'quiz' : 'board') as 'quiz' | 'board',
      classroom_id: classroomId
    };
    try {
      await linkMutation.mutateAsync({
        classGroupId: classroomId,
        data: {
          contentId,
          contentType
        }
      });
      await invalidateClassroomMutationQueries(queryClient, classroomId);
      trackEvent('quiz_classroom_content_autolink_save', {
        ...trackBase,
        result: 'success'
      });
      toast.custom(<ClassroomContentLinkedToast classroomId={classroomId} contentType={contentType} classroomPath={classroomPath} />, {
        duration: 2500,
        position: 'top-center'
      });
    } catch (error) {
      trackEvent('quiz_classroom_content_autolink_save', {
        ...trackBase,
        result: 'fail',
        reason: buildFailReason(error)
      });
      // 정형 에러(권한/한도 등)는 공용 핸들러가 사유별 토스트를 띄운다.
      // 처리되지 않은 에러만 일반 실패 토스트로 덮어 무증상 삼킴을 막는다.
      const handled = handleClassroomLinkError(error, key => t(key as any));
      if (!handled) {
        toast.error(t('common:classroomLink.autoLinkFailedMessage'));
      }
    }
  };
  return {
    autoLink,
    isLinking: linkMutation.isPending
  };
}